// Web-portal device linking via the local RMM agent.
//
// When a client user signs into the web portal with their O365 account, the
// browser can't tell which physical machine it's on — but the RMM agent running
// on that same machine can. The agent exposes a loopback-only endpoint
// (http://127.0.0.1:<port>/rmm-link) that returns this device's key; we hand
// that key to /api/devices/link-contact, which associates the device with the
// signed-in contact. This makes "sign in with O365 → your device is matched to
// you" work in the browser, exactly like the desktop app already does.
//
// Best-effort and silent: if no agent is running (no key endpoint answers), or
// the browser blocks loopback access (e.g. Safari), we simply do nothing — the
// desktop-app path and admin assignment remain available.

const API_URL = process.env.REACT_APP_API_URL || "";
const AGENT_PORTS = [47990, 47991, 47992];

async function fetchDeviceKey() {
  for (const port of AGENT_PORTS) {
    try {
      const ctrl = new AbortController();
      const t = setTimeout(() => ctrl.abort(), 1200);
      const r = await fetch(`http://127.0.0.1:${port}/rmm-link`, {
        method: "GET",
        signal: ctrl.signal,
        // Tell Chrome's Private Network Access this is an intentional loopback call.
        // (Ignored by browsers that don't implement PNA.)
        // eslint-disable-next-line no-undef
        ...(typeof Request !== "undefined" && "targetAddressSpace" in Request.prototype
          ? { targetAddressSpace: "private" } : {}),
      });
      clearTimeout(t);
      if (!r.ok) continue;
      const data = await r.json();
      if (data && data.device_key) return data.device_key;
    } catch {
      // port not listening / blocked — try the next
    }
  }
  return null;
}

// token: the portal contact JWT just issued. Returns true if a device was linked.
export async function linkLocalDevice(token) {
  if (!token) return false;
  try {
    const deviceKey = await fetchDeviceKey();
    if (!deviceKey) return false;
    const res = await fetch(`${API_URL}/api/devices/link-contact`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ agent_key: deviceKey }),
    });
    return res.ok;
  } catch {
    return false;
  }
}
